'use client';
import { useEffect, useMemo, useState } from 'react';
import { IWorkHour } from '../../_types';
import { useTranslations } from 'next-intl';
import { useSelector } from 'react-redux';
import { IRedux } from '../../_store/types';
import dayjs from 'dayjs';
import { Box, Button, Text } from '@chakra-ui/react';
import DateSelector from './DateSelector';
import TimeSelector from './TimeSelector';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export function Form({
  scheduleData,
  interval,
  onConfirm,
}: {
  interval: number;
  scheduleData?: { working_hours: IWorkHour[] };

  onConfirm: (time: string) => void;
}) {
  const [time, setTime] = useState<string>('');
  const [selectedDay, setSelectedDate] = useState<dayjs.Dayjs>(dayjs()); // Selected Date
  const [workHours, setWorkHours] = useState<IWorkHour | null>(null); // Selected Day's working hours

  const t = useTranslations();
  const { is_preorder, future_time } = useSelector(
    (state: IRedux) => state.checkout
  );

  const isToday = useMemo(
    () => selectedDay.isSame(dayjs(), 'day'),
    [selectedDay]
  );

  useEffect(() => {
    if (future_time) {
      setSelectedDate(dayjs(future_time));
      setTime(dayjs(future_time).format('YYYY-MM-DD HH:mm:ss'));
    }
  }, [is_preorder, future_time]);

  const onTimeSelect = (value: string) => {
    setTime(value);
  };

  const handleDateSelect = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
    if (scheduleData?.working_hours) {
      const scheduleOfDay = scheduleData?.working_hours?.[date.get('day')];

      if (scheduleOfDay) setWorkHours(scheduleOfDay);
    }
  };

  return (
    <Box px={4}>
      <Text color={'text'} fontSize='xl' fontWeight={700} mb={4}>
        {t('preorder_title')}
      </Text>
      <DateSelector value={selectedDay} onDateSelect={handleDateSelect} />
      <TimeSelector
        interval={interval}
        value={time}
        isToday={isToday}
        selectedDay={selectedDay}
        workHours={workHours}
        onChange={onTimeSelect}
      />
      <Box
        py={6}
        w='full'
        left={0}
        bottom={0}
        position='sticky'
        bg={'background.card'}
      >
        <Button
          height='56px'
          width='full'
          alignSelf='end'
          bg='primary'
          color='primary.text'
          _hover={{
            bg: 'primary.hover',
          }}
          mt='8px'
          rounded={16}
          size='xl'
          onClick={() => onConfirm(time)}
        >
          {t('save')}
        </Button>
      </Box>
    </Box>
  );
}
